import React from 'react';
import { useNavigate } from 'react-router-dom';
import ClassGate from './ClassGate';

const RESOURCES = [
  {
    icon: 'bi-grid',
    label: 'Índice del Curso',
    description: 'Todas las sesiones, slides y notebooks organizados por etapa',
    action: () => window.open('/classes/python-ml/index.html', '_blank'),
    available: true,
  },
  {
    icon: 'bi-github',
    label: 'Repositorio GitHub',
    description: 'Código fuente, notebooks y materiales del curso',
    action: () => window.open('https://github.com/gypsaman/Python-ML', '_blank'),
    available: true,
  },
  {
    icon: 'bi-journal-code',
    label: 'Notebooks (Colab)',
    description: 'Notebooks interactivos en Google Colab por sesión',
    action: () => window.open('https://github.com/gypsaman/Python-ML/tree/master/notebooks', '_blank'),
    available: true,
  },
  {
    icon: 'bi-chat-dots',
    label: 'Chat del Curso',
    description: 'Asistente RAG con el material del curso',
    action: null,
    available: false,
  },
];

const STAGES = [
  { badge: 'Etapa 0', title: 'Introducción al Curso', sessions: 1 },
  { badge: 'Etapa 1', title: 'Introducción a Python', sessions: 5 },
  { badge: 'Etapa 2', title: 'Machine Learning Clásico', sessions: 3 },
  { badge: 'Etapa 3', title: 'Deep Learning con Python (PyTorch)', sessions: 14 },
  { badge: 'Etapa 4', title: 'Agentes de Inteligencia Artificial', sessions: 2 },
];

export default function ClassPythonMl() {
  const navigate = useNavigate();

  return (
    <ClassGate classId="python-ml" className="Machine Learning con Python">
    <div>
      <button
        onClick={() => navigate('/classes')}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '0 0 1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          fontSize: '0.875rem',
        }}
      >
        <i className="bi bi-arrow-left" /> Todas las clases
      </button>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <span style={{
            background: 'var(--accent-dim)',
            color: 'var(--accent)',
            fontSize: '0.75rem',
            fontWeight: 700,
            padding: '0.2rem 0.6rem',
            borderRadius: 6,
            letterSpacing: '0.04em',
          }}>
            ML-PY
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Cesar Garcia · 2025</span>
        </div>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.3, marginBottom: '0.6rem' }}>
          Machine Learning<br />con Python
        </h1>
        <p style={{ color: 'var(--text-muted)', maxWidth: 620, lineHeight: 1.6 }}>
          Desde los fundamentos de Python hasta sistemas de IA con agentes. Un recorrido profundo
          por el deep learning moderno: tensores, backpropagation, CNNs, Transformers y agentes.
        </p>
      </div>

      <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem' }}>
        Recursos
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
        {RESOURCES.map((r) => (
          <div
            key={r.label}
            onClick={r.available ? r.action : undefined}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '1.25rem 1.5rem',
              cursor: r.available ? 'pointer' : 'default',
              opacity: r.available ? 1 : 0.45,
              transition: 'border-color 0.15s, background 0.15s',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1rem',
            }}
            onMouseEnter={e => { if (r.available) { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--bg-hover)'; } }}
            onMouseLeave={e => { if (r.available) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; } }}
          >
            <i className={`bi ${r.icon}`} style={{ fontSize: '1.4rem', color: r.available ? 'var(--accent)' : 'var(--text-muted)', marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>
                {r.label}
                {!r.available && <span style={{ fontSize: '0.7rem', marginLeft: '0.5rem', color: 'var(--text-muted)' }}>próximamente</span>}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{r.description}</div>
            </div>
          </div>
        ))}
      </div>

      <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem' }}>
        Contenido
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {STAGES.map((s) => (
          <div
            key={s.badge}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '0.9rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <span style={{
              background: 'var(--accent-dim)',
              color: 'var(--accent)',
              fontSize: '0.7rem',
              fontWeight: 700,
              padding: '0.2rem 0.6rem',
              borderRadius: 4,
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}>
              {s.badge}
            </span>
            <span style={{ fontWeight: 500, flex: 1 }}>{s.title}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {s.sessions} {s.sessions === 1 ? 'sesión' : 'sesiones'}
            </span>
          </div>
        ))}
      </div>
    </div>
    </ClassGate>
  );
}
